import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router';
import { useOidc } from '@nohara-dxdev/react-oidc';

import { CustomerRegistrationForm } from '@/features/companies/components/customer/customer-registration-form';
import { SupplierRegistrationForm } from '@/features/companies/components/supplier/supplier-registration-form';
import { RegistrationTabsSection } from '@/features/companies/components/registration-tabs-section';
import { useCompanyRegistration } from '@/features/companies/hooks/use-company-registration';
import { ConstructionRegistrationForm } from '@/features/companies/components/construction-company/construction-registration-form';
import { WitnessCompanyRegistrationForm } from '@/features/companies/components/witness-company/witness-company-registration-form';
import { usePageExitReset } from '@/features/companies/hooks/use-page-exit-reset';
import { useTabUnsavedChanges } from '@/features/companies/hooks/use-tab-unsaved-changes';
import { useGlobalActions } from '@/features/companies/stores/company-registration.store';
import { checkPermission } from '@/utils/helpers';
import { FunctionName, PermissionAction } from '@/lib/enums';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`companies-tabpanel-${index}`}
      aria-labelledby={`companies-tab-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

export const CompaniesRegisterPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const {
    handleCustomerSubmit,
    handleSupplierSubmit,
    handleConstructionSubmit,
    handleWitnessCompanySubmit,
    handleCancel,
  } = useCompanyRegistration();
  const { resetAllForms } = useGlobalActions();
  const hasUnsavedChanges = useTabUnsavedChanges(activeTab);
  const { permissions } = useOidc();
  const navigate = useNavigate();

  useEffect(() => {
    if (!checkPermission(permissions, FunctionName.COMPANY_MANAGEMENT, PermissionAction.CREATE)) {
      navigate('/404');
    }
  }, [permissions, navigate]);

  // Reset all forms when leaving page or reloading (with warning if unsaved changes)
  usePageExitReset(resetAllForms, true, hasUnsavedChanges);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F9F9FF' }}>
      {/* Registration Tabs */}
      <RegistrationTabsSection activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Form content */}
      <TabPanel value={activeTab} index={0}>
        <CustomerRegistrationForm onSubmit={handleCustomerSubmit} onCancel={handleCancel} />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <SupplierRegistrationForm onSubmit={handleSupplierSubmit} onCancel={handleCancel} />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <ConstructionRegistrationForm onSubmit={handleConstructionSubmit} onCancel={handleCancel} />
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <WitnessCompanyRegistrationForm
          onSubmit={handleWitnessCompanySubmit}
          onCancel={handleCancel}
        />
      </TabPanel>
    </Box>
  );
};

export default CompaniesRegisterPage;
